<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('container.containers') }}
        </span>
        <h3 class="page-title">
          {{ $t('container.containerEdit') }}:
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backToReservations ? '/kontenery/rezerwacje' : '/kontenery')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="danger"
          @click.prevent="closeContainer"
          class="mr-1"
          :disabled="!can_close || closeInProgress">
          <template v-if="!closeInProgress">
            <i class="material-icons">lock</i>
            {{ $t('container.closeContainer') }}
          </template>
          <div
            v-if="closeInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('container.editContainer') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="container-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            v-sticky-tabs
            card>
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('container.containerNumber') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="number"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="number" />

                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.deliveryDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :state="errors.length ? 'invalid' : null"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-tab>

            <d-tab :title="$t('product.products')">
              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('product.product') }}</th>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.piecesAmount') }}</th>
                    <th scope="col" class="border-0" style="width: 100px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="products.length">
                  <tr
                    v-for="(product, index) of products"
                    :key="'product-' + index">
                    <td class="align-middle">
                      {{ product.code }} - {{ product.name }}
                    </td>
                    <td class="align-middle">
                      <validation-provider
                        rules="required|min_value:1"
                        :name="'product-amount-' + index"
                        v-slot="{ errors }">
                        <d-input-group>
                          <d-input
                            :min="1"
                            :step="1"
                            :state="errors.length ? 'invalid' : null"
                            :ref="'product-amount-' + index"
                            v-model="product.pivot.quantity"
                            type="number" />
                          <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                        </d-input-group>

                        <d-form-invalid-feedback>
                          {{ errors[0] }}
                        </d-form-invalid-feedback>
                      </validation-provider>
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="danger"
                        @click.prevent="removeProduct(index, product.can_delete)">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('container.noProductsInThisContainer') }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="text-center mt-3">
                <d-button
                  theme="success"
                  class="ml-auto mr-auto pl-5 pr-5"
                  @click.prevent="showAddProductPopup = true;">
                  {{ $t('product.addProduct') }}
                </d-button>
              </div>
            </d-tab>

            <d-tab :title="$t('container.productsInOrders')">
              <d-alert
                :show="!order_product_containers.length"
                theme="warning">
                {{ $t('container.noProductsInOrders') }}
              </d-alert>
              <div
                v-if="order_product_containers.length"
                class="mb-3">
                <table class="table mt-2 mb-0 full-width">
                  <thead class="bg-light">
                    <tr>
                      <th scope="col" class="border-0">{{ $t('product.product') }}</th>
                      <th scope="col" class="border-0" style="width: 200px;">{{ $t('container.canClose') }}</th>
                      <th scope="col" class="border-0" style="width: 150px;">{{ $t('container.ordersSum') }}</th>
                      <th scope="col" class="border-0" style="width: 100px;">{{ $t('ui.availability') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(product, subindex) of order_product_containers"
                      :key="'product-' + subindex">
                      <td class="align-middle">
                        <template v-if="product.order_product">
                        {{ product.order_product.code }} - {{ product.order_product.name }}
                        </template>
                        <template v-else>
                          &mdash;
                        </template>
                      </td>
                      <td class="align-middle">
                        <span
                          v-if="product.can_close"
                          class="text-success">
                          {{ $t('ui.yes') }}
                        </span>
                        <span
                          v-if="!product.can_close"
                          class="text-danger">
                          {{ $t('ui.no') }}
                        </span>
                      </td>
                      <td class="align-middle">
                        {{ product.sum_quantity }} {{ $t('ui.piecesAbbr') }}
                      </td>
                      <td class="align-middle">
                        {{ product.product_availability }} {{ $t('ui.piecesAbbr') }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}.
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <add-product-to-container-popup
      v-if="showAddProductPopup" />
  </d-container>
</template>

<script>
import AddProductToContainerPopup from './AddProductToContainerPopup.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'container-edit',
  components: {
    'add-product-to-container-popup': AddProductToContainerPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    backToReservations () {
      return !!this.$route.params.fromreservations;
    }
  },
  data() {
    return {
      number: '',
      date: '',
      can_close: false,
      order_product_containers: [],
      products: [],
      allProducts: [],
      loadedData: false,
      loadError: false,
      closeInProgress: false,
      showAddProductPopup: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/products/containers/item/',
      instanceFields: ['number', 'date', 'products', 'can_close', 'order_product_containers'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      params: {
        with: ['products', 'orderProductContainers']
      },
      successAction: () => {
        this.date = new Date(this.date);

        if (!this.products) {
          this.products = [];
        }

        if (!this.order_product_containers) {
          this.order_product_containers = [];
        }
      }
    });

    this.$bus.$on('close-add-product-to-container-popup', this.closeAddProductPopup);
    this.$bus.$on('container-add-product', this.addProductToContainer);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['container-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        number: this.number,
        date: this.date ? [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        products: this.prepareProducts()
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/containers/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('container.updateContainerSuccessTitle'),
        successText: this.$t('container.updateContainerSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('container.updateContainerErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            method: 'get',
            endpointBase: '/products/containers/item/',
            instanceFields: ['number', 'date', 'products'],
            loadedKey: 'loadedData',
            errorKey: 'loadError',
            params: {
              with: ['products']
            },
            successAction: () => {
              this.date = new Date(this.date);

              if (!this.products) {
                this.products = [];
              }
            }
          });
        }
      });
    },
    removeProduct (index, canDelete) {
      if (!canDelete) {
        Vue.swal({
          title: this.$t('order.exisitngProductsInHplOrder'),
          html: this.$t('container.removeProductText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.products.splice(index, 1);
    },
    prepareProducts () {
      let output = {};
      let onlyIdsWithQuantity = this.products.map(product => ({
        id: product.id,
        quantity: parseInt(product.pivot.quantity, 10)
      }));

      for (let i = 0; i < onlyIdsWithQuantity.length; i++) {
        let product = onlyIdsWithQuantity[i];
        output[product.id] = product.quantity;
      }

      return output;
    },
    closeContainer () {
      this.closeInProgress = true;

      FormUtils.submitInBackground({
        method: 'get',
        $http: this.$http,
        endpoint: '/products/containers/close/' + this.id,
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('container.containerCloseErrorText'),
        successAction: () => {
          this.closeInProgress = false;
          this.$router.push(this.backToReservations ? '/kontenery/rezerwacje' : '/kontenery');
        },
        failAction: () => {
          this.closeInProgress = false;
        }
      });
    },
    closeAddProductPopup () {
      this.showAddProductPopup = false;
    },
    addProductToContainer (dataToUse) {
      this.products.push({
        id: dataToUse.product_id,
        pivot: {
          quantity: dataToUse.qty,
        },
        name: dataToUse.product_name,
        code: dataToUse.product_code,
        can_delete: true
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('close-add-product-to-container-popup', this.closeAddProductPopup);
    this.$bus.$off('container-add-product', this.addProductToContainer);
  }
};
</script>
